<template>
  <div v-loading="loading">
    <div class="row mb-2" v-if="validationErrors">
      <div class="col">
        <el-alert type="error">
          {{ validationErrors }}
        </el-alert>
      </div>
    </div>

    <div class="row">
      <div class="col-lg-12">
        <el-alert
          v-if="!leadEvent.is_pipeline_attached"
          title="Alert"
          type="info"
          description="This event is not currently synchronized with HubSpot. Clicking on the 'Save' button will update this event as a deal in HubSpot. Kindly ensure that the deal stage is accurately specified."
          show-icon>
        </el-alert>
      </div>
    </div>

    <div class="row mt-3">
      <div class="col-lg-6">
        <base-input label="Name">
          <el-input v-model="form.name" placeholder="Event Name"></el-input>
        </base-input>
      </div>
      <div class="col-lg-6">
        <base-input label="Event Type">
          <el-select v-model="form.event_type" placeholder="Event Type">
            <el-option
              v-for="(label, key) in eventTypes"
              :key="key"
              :label="label"
              :value="key">
            </el-option>
          </el-select>
        </base-input>
      </div>
    </div>

    <div class="row mt-3">
      <div class="col-lg-6">
        <base-input label="Pipeline">
          <el-select v-model="form.hubspot_deal_pipeline_id" disabled placeholder="Pipeline">
            <el-option
              v-for="item in pipelines"
              :key="'pipeline_' + item.id"
              :label="item.name"
              :value="item.id">
            </el-option>
          </el-select>
        </base-input>
      </div>
      <div class="col-lg-6">
        <base-input label="Pipeline Stage">
          <el-select v-model="form.hubspot_deal_stage_id" placeholder="Pipeline Stage">
            <el-option
              v-for="item in selectedDealPipeline?.stages"
              :key="'pipeline_stage_' + item.id"
              :label="item.name"
              :value="item.id">
            </el-option>
          </el-select>
        </base-input>
      </div>
    </div>
    
    <div class="row mt-3">
        <div class="col-lg-12">
             <el-alert type="warning" v-if="pipelineEventTypeMismatch" >
                The connected pipeline in Hubspot does not align with the event type. We will attach the appropriate pipeline, though please note that pipeline stage information will be lost during this process.
            </el-alert>
        </div>
    </div>

    <div class="row mt-4">
      <div class="col-12 text-right">
        <base-button 
          type="primary" 
          @click="savePipeline"
          :loading="loading"
        >
          Save
        </base-button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  Select,
  Option,
  Alert,
  Notification,
  Input
} from "element-ui";
import { mapState } from "vuex";
import LeadEventService from '@/services/lead-event.service';
import settingService from '@/services/settings.service';
import { EnquiryService } from "@/services";
import { eventTypes } from '@/../app.config';

export default {
  name: 'EditPipeline',
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [Alert.name]: Alert,
    [Input.name]: Input,
  },
  props: {
    leadEvent: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      validationErrors: '',
      pipelines: null,
      eventTypes: eventTypes,
      pipelineEventTypeMismatch: false,
      form: {
        name: this.leadEvent.name,
        hubspot_deal_pipeline_id: this.leadEvent.hubspot_deal_pipeline_id,
        hubspot_deal_stage_id: this.leadEvent.hubspot_deal_stage_id,
        event_type: this.leadEvent.event_type,
      },
    };
  },
  computed: {
    ...mapState("country/", {
      country: (state) => state.country || 'AU',
    }),
    selectedDealPipeline: function () {
      if (!this.pipelines) {
        return [];
      }
      // Find pipeline based on event type
      let pipeline = this.pipelines.find((item) => item.event_types.indexOf(this.form.event_type) !== -1);
      return pipeline;
    },
  },
  created() {
    this.getDealPipelines();
  },
  methods: {
    getDealPipelines() {
      let pipelines = settingService.getDealPipelines(this.country);
      pipelines.then(data => {
        this.pipelines = data.data;
        this.checkPipelineMismatch();
      });
    },
    checkPipelineMismatch() {
        // Logic adapted from Basic.vue to check for mismatch
        // Since we are just editing pipeline here, we might not need the full event type change logic
        // But we should ensure the selected pipeline matches the event type
        
        if (this.selectedDealPipeline && this.form.hubspot_deal_pipeline_id !== this.selectedDealPipeline.id) {
             // If the current pipeline ID doesn't match the one for the event type
             // This might happen if event type changed but pipeline didn't update yet?
             // Or if it's a legacy event.
             
             // For now, let's just default to the correct pipeline if not set or mismatch
             if (!this.form.hubspot_deal_pipeline_id) {
                 this.form.hubspot_deal_pipeline_id = this.selectedDealPipeline.id;
             }
        }
    },
    async savePipeline() {
      this.loading = true;
      this.validationErrors = '';
      
      try {
        // We use updateDealStage endpoint if only stage is changing, 
        // or updateLeadEventData if we need to update pipeline too?
        // LeadEventService.updateDealStage(leadEventId, dealStageId) exists.
        // But if pipeline ID changes (e.g. mismatch fix), we might need more.
        
        // Let's use updateLeadEventData to be safe as it handles general updates
        // Or check if there is a specific endpoint.
        // Basic.vue used updateLeadEventData.
        
        const data = {
            name: this.form.name,
            event_type: this.form.event_type,
            hubspot_deal_pipeline_id: this.form.hubspot_deal_pipeline_id,
            hubspot_deal_stage_id: this.form.hubspot_deal_stage_id
        };

        await LeadEventService.updateLeadEventData(this.leadEvent.id, data);
        
        // Refresh lead events in store
        await this.$store.dispatch("leads/getLeadEvents", this.leadEvent.lead_id);
        
        this.$emit('onSave');
        
        Notification.success({
          title: 'Success',
          message: 'Pipeline settings saved successfully!',
          type: 'success',
        });
        
      } catch (error) {
        console.error('Error saving pipeline:', error);
        this.validationErrors = error.response?.data?.error?.message || 'Failed to save pipeline settings.';
         Notification.error({
          title: 'Error',
          message: 'Failed to save pipeline settings.',
          type: 'error',
        });
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    selectedDealPipeline: function() {
      this.$nextTick(() => {
        if (this.form.hubspot_deal_pipeline_id) {
          return;
        }
        if (this.selectedDealPipeline) {
            this.form.hubspot_deal_pipeline_id = this.selectedDealPipeline.id;
        }
      });
    },
    'form.event_type': function() {
        this.checkPipelineMismatch();
    },
    leadEvent: {
        handler(newVal) {
            if(newVal) {
                this.form.name = newVal.name;
                this.form.hubspot_deal_pipeline_id = newVal.hubspot_deal_pipeline_id;
                this.form.hubspot_deal_stage_id = newVal.hubspot_deal_stage_id;
                this.form.event_type = newVal.event_type;
                this.checkPipelineMismatch();
            }
        },
        deep: true
    }
  }
};
</script>
